import { useEffect, useState } from 'react';
import { Dialog, DialogContent, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { InputOTP, InputOTPGroup, InputOTPSlot } from '@/components/ui/input-otp';
import { toast } from 'sonner';
import { Sparkles, ShieldCheck, Mail, CheckCircle2 } from 'lucide-react';
import { subscriptionsApi } from '@/lib/subscriptions';

/**
 * Universal subscribe modal. Used by:
 *   - Journal masthead "Subscribe"
 *   - Community "Join the Newsletter" buttons
 *   - In-article gates
 *   - Footer "Manage emails" → null token path
 *
 * Props:
 *   open: boolean
 *   onClose: () => void
 *   listId: e.g. "journal" | "tax_alerts" | "holiday_hours"
 *   listName?: display label override (default fetched from API)
 *   source: string for analytics e.g. "journal_masthead"
 *   sourceArticleUid?: string
 *   onSuccess?: ({ master_token, preference_url }) => void
 */
export function SubscribeModal({
  open,
  onClose,
  listId = 'journal',
  listName,
  source = 'unknown',
  sourceArticleUid,
  prefillEmail,
  onSuccess,
}) {
  const [step, setStep] = useState('email'); // email | code | success
  const [email, setEmail] = useState('');
  const [firstName, setFirstName] = useState('');
  const [honeypot, setHoneypot] = useState(''); // bot trap
  const [code, setCode] = useState('');
  const [verificationId, setVerificationId] = useState(null);
  const [maskedEmail, setMaskedEmail] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [resolvedListName, setResolvedListName] = useState(listName || 'KGOB');
  const [resendCooldown, setResendCooldown] = useState(0);
  const [preferenceUrl, setPreferenceUrl] = useState('');

  // Reset state when modal closes
  useEffect(() => {
    if (!open) {
      const t = setTimeout(() => {
        setStep('email');
        setEmail('');
        setFirstName('');
        setHoneypot('');
        setCode('');
        setVerificationId(null);
        setMaskedEmail('');
        setError('');
        setPreferenceUrl('');
      }, 300);
      return () => clearTimeout(t);
    }
    // Apply prefill when modal opens
    if (open && prefillEmail && !email) {
      setEmail(prefillEmail);
    }
  }, [open, prefillEmail, email]);

  // Fetch list display name if not provided
  useEffect(() => {
    if (listName) {
      setResolvedListName(listName);
      return;
    }
    subscriptionsApi.getLists().then(({ ok, data }) => {
      if (ok) {
        const item = (data.lists || []).find((l) => l._id === listId);
        if (item) setResolvedListName(item.name);
      }
    });
  }, [listId, listName]);

  // Cooldown ticker for resend button
  useEffect(() => {
    if (resendCooldown <= 0) return;
    const t = setTimeout(() => setResendCooldown((c) => c - 1), 1000);
    return () => clearTimeout(t);
  }, [resendCooldown]);

  async function submitEmail(e) {
    e?.preventDefault();
    setError('');
    if (!email.trim() || !email.includes('@') || email.length < 5) {
      setError('Please enter a valid email address.');
      return;
    }
    setSubmitting(true);
    try {
      const { ok, data } = await subscriptionsApi.request({
        email: email.trim(),
        list_id: listId,
        first_name: firstName.trim() || null,
        source,
        source_article_uid: sourceArticleUid || null,
        website: honeypot,
      });
      if (!ok || !data.ok) {
        if (data.reason === 'rate_limit_ip' || data.reason === 'rate_limit_email') {
          setError("You've requested too many codes recently. Please wait 15 minutes.");
        } else if (data.reason === 'unknown_list') {
          setError('This list is unavailable right now.');
        } else if (data.reason === 'invalid_email') {
          setError('That email looks off — please double-check.');
        } else {
          setError('Something went wrong. Please try again.');
        }
        return;
      }
      if (data.already_subscribed) {
        toast.success("You're already on the list — check your inbox for past issues.");
        setStep('success');
        return;
      }
      setVerificationId(data.verification_id);
      setMaskedEmail(data.masked_email || email);
      setStep('code');
      setResendCooldown(45);
    } catch (err) {
      setError('Network error — please try again.');
    } finally {
      setSubmitting(false);
    }
  }

  async function submitCode(e) {
    e?.preventDefault();
    setError('');
    if (!/^\d{6}$/.test(code)) {
      setError('Please enter the full 6-digit code.');
      return;
    }
    setSubmitting(true);
    try {
      const { ok, data } = await subscriptionsApi.verify({
        verification_id: verificationId,
        code,
      });
      if (!ok || !data.ok) {
        const map = {
          code_mismatch: "That code doesn't match. Please try again.",
          code_expired: 'That code expired. Request a new one.',
          too_many_attempts: 'Too many attempts. Please request a new code.',
          already_processed: 'This code was already used.',
          verification_not_found: 'Verification not found. Please start over.',
        };
        setError(map[data.reason] || 'Verification failed. Please try again.');
        if (['code_expired', 'too_many_attempts', 'verification_not_found'].includes(data.reason)) {
          setStep('email');
          setCode('');
        }
        return;
      }
      setPreferenceUrl(data.preference_url || '');
      try {
        if (data.master_token) {
          localStorage.setItem('kgob_subscriber_token', data.master_token);
        }
      } catch (_) { /* no action needed */ }
      setStep('success');
      onSuccess && onSuccess(data);
    } catch (err) {
      setError('Network error — please try again.');
    } finally {
      setSubmitting(false);
    }
  }

  async function resendCode() {
    if (resendCooldown > 0) return;
    setError('');
    setSubmitting(true);
    try {
      const { ok, data } = await subscriptionsApi.request({
        email: email.trim(),
        list_id: listId,
        first_name: firstName.trim() || null,
        source: `${source}_resend`,
        source_article_uid: sourceArticleUid || null,
        website: honeypot,
      });
      if (ok && data.ok && data.verification_id) {
        setVerificationId(data.verification_id);
        toast.success('New code sent.');
        setResendCooldown(60);
      } else {
        setError('Could not resend right now. Please wait a moment.');
      }
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose && onClose()}>
      <DialogContent
        className="sm:max-w-md p-0 overflow-hidden bg-[#F7F6F2] border-[#E1DFD7]"
        data-testid="subscribe-modal"
      >
        <DialogTitle className="sr-only">{resolvedListName} newsletter signup</DialogTitle>
        {/* Brand bar */}
        <div className="bg-[#0B2240] px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Sparkles className="w-4 h-4 text-[#D4AF37]" />
            <span className="text-[10px] tracking-[0.22em] uppercase text-[#D4AF37] font-semibold">
              {resolvedListName}
            </span>
          </div>
          <span className="text-[10px] tracking-[0.18em] uppercase text-white/50">
            {step === 'email' && 'Step 1 of 2'}
            {step === 'code' && 'Step 2 of 2'}
            {step === 'success' && 'Confirmed'}
          </span>
        </div>

        <div className="px-6 sm:px-8 py-6">
          {step === 'email' && (
            <form onSubmit={submitEmail} className="space-y-4" data-testid="subscribe-step-email">
              <h2 className="font-['Playfair_Display'] text-2xl text-[#0B2240] leading-tight">
                Join {resolvedListName}.
              </h2>
              <p className="text-sm text-[#4C5A70] leading-relaxed">
                Enter your email — we&rsquo;ll send a 6-digit code to confirm. We never sell or share email. One-click unsubscribe in every issue.
              </p>

              {/* Honeypot — invisible to humans, bots fill it */}
              <input data-testid="subscribe-modal-input-1"
                type="text"
                name="website"
                tabIndex={-1}
                autoComplete="off"
                value={honeypot}
                onChange={(e) => setHoneypot(e.target.value)}
                style={{ position: 'absolute', left: '-9999px', opacity: 0, height: 1, width: 1 }}
                aria-hidden="true"
              />

              <div className="space-y-3">
                <Input
                  type="text"
                  placeholder="First name (optional)"
                  value={firstName}
                  maxLength={80}
                  onChange={(e) => setFirstName(e.target.value)}
                  data-testid="subscribe-first-name"
                  className="bg-white border-[#E1DFD7] focus-visible:ring-[#D4AF37]"
                />
                <Input
                  type="email"
                  required
                  placeholder="you@firm.com"
                  value={email}
                  maxLength={254}
                  onChange={(e) => {
                    setEmail(e.target.value);
                    setError('');
                  }}
                  data-testid="subscribe-email"
                  className="bg-white border-[#E1DFD7] focus-visible:ring-[#D4AF37]"
                />
              </div>

              {error && (
                <div
                  className="text-sm text-[#B45F3E] bg-[#FBEEE4] border border-[#E8C7B0] rounded px-3 py-2"
                  data-testid="subscribe-error"
                >
                  {error}
                </div>
              )}

              <Button
                type="submit"
                disabled={submitting}
                className="w-full h-12 bg-[linear-gradient(180deg,#E8D89E_0%,#D4AF37_50%,#B99610_100%)] text-[#0B2240] font-semibold tracking-wide hover:opacity-95"
                data-testid="subscribe-send-code-btn"
              >
                {submitting ? 'Sending…' : 'Send me a code'}
              </Button>

              <p className="text-[11px] text-[#4C5A70]/70 leading-relaxed pt-2 flex items-start gap-2">
                <ShieldCheck className="w-3.5 h-3.5 mt-0.5 text-[#D4AF37] flex-shrink-0" />
                <span>
                  By subscribing you agree to receive email from KGOB. Unsubscribe in one click from any message.
                </span>
              </p>
            </form>
          )}

          {step === 'code' && (
            <form onSubmit={submitCode} className="space-y-5" data-testid="subscribe-step-code">
              <div className="flex items-center gap-2 text-[#D4AF37]">
                <Mail className="w-4 h-4" />
                <span className="text-[11px] tracking-[0.2em] uppercase font-semibold">Check your inbox</span>
              </div>
              <h2 className="font-['Playfair_Display'] text-2xl text-[#0B2240] leading-tight">
                Enter the code we sent to <span className="text-[#0B2240]">{maskedEmail}</span>.
              </h2>
              <p className="text-sm text-[#4C5A70] leading-relaxed">
                Six digits. Expires in 30 minutes. Codes can take a few seconds to arrive.
              </p>

              <div className="flex justify-center py-2">
                <InputOTP
                  maxLength={6}
                  value={code}
                  onChange={(v) => {
                    setCode(v);
                    setError('');
                  }}
                  data-testid="subscribe-code-input"
                >
                  <InputOTPGroup>
                    {[0, 1, 2, 3, 4, 5].map((i) => (
                      <InputOTPSlot key={i} index={i} className="w-11 h-12 text-lg border-[#E1DFD7] focus:ring-[#D4AF37]" />
                    ))}
                  </InputOTPGroup>
                </InputOTP>
              </div>

              {error && (
                <div
                  className="text-sm text-[#B45F3E] bg-[#FBEEE4] border border-[#E8C7B0] rounded px-3 py-2"
                  data-testid="subscribe-error"
                >
                  {error}
                </div>
              )}

              <Button
                type="submit"
                disabled={submitting || code.length !== 6}
                className="w-full h-12 bg-[linear-gradient(180deg,#E8D89E_0%,#D4AF37_50%,#B99610_100%)] text-[#0B2240] font-semibold tracking-wide hover:opacity-95 disabled:opacity-60"
                data-testid="subscribe-verify-btn"
              >
                {submitting ? 'Confirming…' : 'Confirm'}
              </Button>

              <div className="flex items-center justify-between pt-1">
                <button
                  type="button"
                  onClick={() => {
                    setStep('email');
                    setCode('');
                    setError('');
                  }}
                  className="text-xs text-[#4C5A70] hover:text-[#0B2240] underline-offset-2 hover:underline"
                  data-testid="subscribe-change-email"
                >
                  Wrong email?
                </button>
                <button
                  type="button"
                  onClick={resendCode}
                  disabled={resendCooldown > 0 || submitting}
                  className="text-xs text-[#4C5A70] hover:text-[#0B2240] disabled:opacity-50 underline-offset-2 hover:underline"
                  data-testid="subscribe-resend-code"
                >
                  {resendCooldown > 0 ? `Resend in ${resendCooldown}s` : 'Resend code'}
                </button>
              </div>
            </form>
          )}

          {step === 'success' && (
            <div className="space-y-5 text-center" data-testid="subscribe-step-success">
              <div className="mx-auto w-14 h-14 rounded-full bg-[#D4AF37]/15 flex items-center justify-center">
                <CheckCircle2 className="w-8 h-8 text-[#D4AF37]" />
              </div>
              <h2 className="font-['Playfair_Display'] text-2xl text-[#0B2240]">You&rsquo;re on the list.</h2>
              <p className="text-sm text-[#4C5A70] leading-relaxed">
                Your first issue of <em>{resolvedListName}</em> will arrive in your inbox. We send sparingly — only when there&rsquo;s something worth your time.
              </p>
              {preferenceUrl && (
                <p className="text-xs text-[#4C5A70]/80 pt-1">
                  Manage your preferences anytime:{' '}
                  <a
                    href={preferenceUrl}
                    className="underline hover:text-[#0B2240]"
                    data-testid="subscribe-preference-link"
                  >
                    Email preferences
                  </a>
                </p>
              )}
              <Button
                type="button"
                onClick={onClose}
                className="w-full h-11 bg-[#0B2240] text-white hover:bg-[#0B2240]/90"
                data-testid="subscribe-close-success"
              >
                Keep reading
              </Button>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default SubscribeModal;
